import { useState } from 'react';
import { cn } from './cn.js';

/**
 * The GoBeeVir brand mark.
 *
 * Served from `/brand/` rather than imported as a module on purpose: a missing
 * import would fail the build, and a brand asset going astray should degrade to
 * a lettermark, not take the whole app down. If the file is absent the `onError`
 * path renders the same "G" tile the app used before the logo existed, so every
 * screen keeps working either way.
 *
 * Two files, because they are different jobs:
 *
 *   mark     the beaver-B alone, for anywhere under ~40px — the wordmark is
 *            unreadable at nav size and just turns into mud
 *   lockup   mark + "GoBeeVir" wordmark, for sign-in screens and documents
 *
 * Drop them at `brand/` in the repo root and run `pnpm brand:sync`.
 */

export type LogoVariant = 'mark' | 'lockup';

/**
 * Candidates, tried in order until one loads.
 *
 * SVG first because it is the right format for something rendered from 16px in
 * a browser tab to full size on a printed invoice — but PNG is what a designer
 * usually hands over, and refusing it would mean the logo silently never
 * appears. The lockup falls back to the mark, so supplying one file is enough
 * to get the brand on screen everywhere.
 */
const SOURCES: Record<LogoVariant, string[]> = {
  mark: [
    '/brand/gobeevir-mark.svg',
    '/brand/gobeevir-mark.png',
    '/brand/gobeevir-logo.svg',
    '/brand/gobeevir-logo.png',
  ],
  lockup: [
    '/brand/gobeevir-logo.svg',
    '/brand/gobeevir-logo.png',
    '/brand/gobeevir-mark.svg',
    '/brand/gobeevir-mark.png',
  ],
};

export function Logo({
  variant = 'mark',
  size = 26,
  className,
  /** Wordmark colour for the fallback only; the real asset carries its own. */
  tone = 'dark',
  showWordmark,
  wordmarkSize,
}: {
  variant?: LogoVariant;
  /** Height in pixels. Width follows the asset's own aspect ratio. */
  size?: number;
  className?: string;
  tone?: 'dark' | 'light';
  /** Render "GoBeeVir" beside the mark. Ignored when variant is 'lockup'. */
  showWordmark?: boolean;
  /**
   * Size the wordmark independently of the mark. They scale together by
   * default, which is right nearly everywhere — pass this only where a
   * specific screen needs the balance set by eye.
   */
  wordmarkSize?: number;
}) {
  // Walk the candidate list; only give up on the lettermark once every
  // filename has been tried.
  const [attempt, setAttempt] = useState(0);
  const candidates = SOURCES[variant];
  // Wordmark scales with the mark: a 104px logo beside 15px text reads as a
  // mistake. 0.3 leaves the 26px nav header exactly where it was.
  const wordSize = wordmarkSize ?? Math.max(15, Math.round(size * 0.3));
  const failed = attempt >= candidates.length;

  if (failed) {
    return (
      <span className={cn('flex items-center gap-2.5', className)}>
        <span
          className="grid shrink-0 place-items-center rounded-[7px] bg-brand-orange font-bold leading-none text-white"
          style={{ height: size, width: size, fontSize: size * 0.54 }}
          aria-hidden
        >
          G
        </span>
        {(showWordmark || variant === 'lockup') && (
          <Wordmark size={wordSize} tone={tone} />
        )}
      </span>
    );
  }

  return (
    <span className={cn('flex items-center gap-2.5', className)}>
      {/*
        The mark is drawn as a cut-out: the beaver head and belly are transparent
        so the white page shows through. On a dark surface that reads as a hollow
        animal, and no amount of resizing fixes it. Giving it the white ground it
        was drawn for restores the artwork exactly, and reads as a deliberate
        app-icon tile rather than a patch.
      */}
      <img
        // Keyed on the attempt so React remounts the element and actually
        // retries the next candidate rather than reusing the errored one.
        key={candidates[attempt]}
        src={candidates[attempt]}
        alt="GoBeeVir"
        height={size}
        style={
          variant === 'mark'
            ? // The mark is square, so give it a box and let it fit inside.
              // `width: auto` collapses to 0 on an SVG that carries only a
              // viewBox and no intrinsic width — which is most exported SVGs.
              { height: size, width: size, objectFit: 'contain' }
            : // The lockup is wide and its ratio varies, so height drives it —
              // but keep a floor so a dimensionless file still shows.
              { height: size, width: 'auto', minWidth: size }
        }
        className={cn(
          'shrink-0 select-none',
          // White ground only where it is needed: a dark surface, and only for
          // the mark. The lockup and light surfaces are untouched.
          variant === 'mark' && tone === 'dark' && 'rounded-[22%] bg-white p-[3%]',
        )}
        onError={() => setAttempt((n) => n + 1)}
        draggable={false}
      />
      {showWordmark && variant === 'mark' && (
        <Wordmark size={wordSize} tone={tone} />
      )}
    </span>
  );
}

/**
 * The wordmark, as artwork rather than as text.
 *
 * The brand sets 'GoBeeVir' in a specific serif with an orange 'Go' — system
 * font-weight-600 next to it is recognisably not the brand. Two files, because
 * the supplied artwork sets 'BeeVir' in navy and that half vanishes on a dark
 * panel; the light variant recolours it to white and leaves the orange alone.
 *
 * Falls back to text on the same principle as the mark: a missing asset should
 * cost you the typeface, not the name.
 */
function Wordmark({ size, tone }: { size: number; tone: 'dark' | 'light' }) {
  const candidates =
    tone === 'dark'
      ? ['/brand/gobeevir-wordmark-light.svg', '/brand/gobeevir-wordmark-light.png']
      : ['/brand/gobeevir-wordmark.svg', '/brand/gobeevir-wordmark.png'];

  const [attempt, setAttempt] = useState(0);

  if (attempt >= candidates.length) {
    return (
      <span
        className={cn(
          'font-semibold tracking-tight',
          tone === 'dark' ? 'text-white' : 'text-brand-navy',
        )}
        style={{ fontSize: size }}
      >
        GoBeeVir
      </span>
    );
  }

  return (
    <img
      key={candidates[attempt]}
      src={candidates[attempt]}
      alt='GoBeeVir'
      // Height drives it; the wordmark is a wide strip and its ratio is fixed.
      style={{ height: size, width: 'auto' }}
      className='shrink-0 select-none'
      onError={() => setAttempt((n) => n + 1)}
      draggable={false}
    />
  );
}
